import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type PaymentStatus, type InvoiceDetailDto } from '@/api/admin'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import { Button } from '@/components/ui/button'
import { EmailPreviewModal } from '@/components/shared/EmailPreviewModal'
import { InvoiceBookingPicker } from './InvoiceBookingPicker'
import { Printer, Send, Check, Bell, Download, RefreshCw, ExternalLink, Pencil } from 'lucide-react'
import { formatMoney, currencySymbol } from '@/lib/money'
import { useAdminDates } from '@/hooks/useDates'
import { formatAddress } from '@/lib/address'

const PAYMENT_STATUSES: PaymentStatus[] = ['Unpaid', 'PartiallyPaid', 'Paid', 'Void']
const paymentLabel: Record<PaymentStatus, string> = {
  Unpaid: 'Unpaid', PartiallyPaid: 'Partially paid', Paid: 'Paid', Void: 'Void',
}
const paymentColour: Record<PaymentStatus, string> = {
  Unpaid: 'text-red-700 bg-red-50',
  PartiallyPaid: 'text-yellow-700 bg-yellow-50',
  Paid: 'text-green-700 bg-green-50',
  Void: 'text-gray-500 bg-gray-100',
}

async function downloadPdf(id: string, invoiceNumber: string) {
  const res = await adminApi.downloadInvoicePdf(id)
  const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${invoiceNumber}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

export default function InvoicePage() {
  const { formatDate } = useAdminDates()
  const fmt = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'long', year: 'numeric' })
  }
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => adminApi.getInvoice(id!).then(r => r.data),
    enabled: !!id,
  })

  const [sendConfirm, setSendConfirm] = useState(false)
  const [reminderOpen, setReminderOpen] = useState(false)

  const { data: emailTemplates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => adminApi.getEmailTemplates().then(r => r.data),
  })

  const reminderTemplate = emailTemplates?.find(t => t.type === 'invoice_reminder')

  const reminderDefaults = useMemo(() => {
    if (!inv || !reminderTemplate) return { subject: '', body: '' }
    const sub = (s: string) => s
      .replace(/£\{\{amount\}\}/g, '{{currency_symbol}}{{amount}}')
      .replace(/\{\{currency_symbol\}\}/g, currencySymbol(inv.currency))
      .replace(/\{\{companyName\}\}/g, inv.companyDisplayName ?? '')
      .replace(/\{\{contactName\}\}/g, inv.clientContactName ?? inv.clientName)
      .replace(/\{\{invoiceNumber\}\}/g, inv.invoiceNumber)
      .replace(/\{\{courseName\}\}/g, inv.lines.map(l => l.courseName).join(', '))
      .replace(/\{\{amount\}\}/g, inv.amount.toFixed(2))
      .replace(/\{\{dueDate\}\}/g, inv.dueDate ? fmt(inv.dueDate) : 'N/A')
    return { subject: sub(reminderTemplate.subject), body: sub(reminderTemplate.body) }
  }, [inv, reminderTemplate])

  const { data: xeroStatus } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => adminApi.getXeroStatus().then(r => r.data),
  })

  const pushToXeroMutation = useMutation({
    mutationFn: () => adminApi.pushInvoiceToXero(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice', id] }),
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      alert(msg ?? 'Failed to push invoice to Xero.')
    },
  })

  const reminderMutation = useMutation({
    mutationFn: ({ subject, body }: { subject: string; body: string }) =>
      adminApi.sendInvoiceReminder(id!, { subject, body }),
    onSuccess: () => setReminderOpen(false),
    onError: () => alert('Failed to send reminder.'),
  })

  const sendMutation = useMutation({
    mutationFn: () => adminApi.sendInvoice(id!),
    onSuccess: () => setSendConfirm(false),
    onError: () => alert('Failed to send invoice email.'),
  })

  const [showBookingPicker, setShowBookingPicker] = useState(false)
  const [bookingPickerError, setBookingPickerError] = useState<string | null>(null)
  const updateBookingsMutation = useMutation({
    mutationFn: (bookingIds: string[]) => adminApi.updateInvoiceBookings(id!, bookingIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['all-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      setShowBookingPicker(false)
    },
    onError: () => setBookingPickerError('Failed to update the invoice’s bookings. Please try again.'),
  })

  const updateMutation = useMutation({
    mutationFn: (status: PaymentStatus) =>
      adminApi.updateInvoice(id!, { paymentStatus: status, dueDate: inv?.dueDate ?? null, notes: inv?.notes ?? null }),
    onMutate: async (newStatus) => {
      await queryClient.cancelQueries({ queryKey: ['invoice', id] })
      const snapshot = queryClient.getQueryData<InvoiceDetailDto>(['invoice', id])
      queryClient.setQueryData<InvoiceDetailDto>(['invoice', id], old =>
        old ? { ...old, paymentStatus: newStatus } : old)
      return { snapshot }
    },
    onError: (_err, _newStatus, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(['invoice', id], ctx.snapshot)
      alert('Failed to update payment status.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['all-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    },
  })

  useTopBar(
    <Breadcrumb items={[
      { label: 'Invoices', to: '/admin/invoices' },
      { label: inv ? `${inv.invoiceNumber} — ${inv.clientName}` : '…' },
    ]} />
  )

  if (isLoading) return <p className="text-gray-500 p-6">Loading…</p>
  if (!inv) return <p className="text-gray-500 p-6">Invoice not found.</p>

  const companyAddress = formatAddress({
    line1: inv.companyAddressLine1, line2: inv.companyAddressLine2, city: inv.companyCity,
    county: inv.companyCounty, postcode: inv.companyPostcode, country: inv.companyCountry,
  }, inv.region)

  const clientAddress = formatAddress({
    line1: inv.clientAddressLine1, line2: inv.clientAddressLine2, city: inv.clientCity,
    county: inv.clientCounty, postcode: inv.clientPostcode, country: inv.clientCountry,
  }, inv.region, '\n')

  return (
    <>
    <EmailPreviewModal
      isOpen={reminderOpen}
      onClose={() => setReminderOpen(false)}
      onSend={(subject, body) => reminderMutation.mutate({ subject, body })}
      isSending={reminderMutation.isPending}
      defaultSubject={reminderDefaults.subject}
      defaultBody={reminderDefaults.body}
      recipientEmail={inv?.clientContactEmail ?? ''}
      title="Send payment reminder"
    />
    {showBookingPicker && (
      <InvoiceBookingPicker
        clientId={inv.clientId}
        initiallySelected={inv.lines.map(l => l.bookingId)}
        excludeInvoiceId={inv.id}
        title="Edit bookings on this invoice"
        confirmLabel="Save"
        pending={updateBookingsMutation.isPending}
        error={bookingPickerError}
        onConfirm={bookingIds => updateBookingsMutation.mutate(bookingIds)}
        onClose={() => setShowBookingPicker(false)}
      />
    )}
    <div>
      {/* ── Screen-only toolbar ── */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center gap-2">
          {/* Payment status selector */}
          <select
            value={inv.paymentStatus}
            onChange={e => updateMutation.mutate(e.target.value as PaymentStatus)}
            className={`text-sm rounded-md border border-gray-200 px-3 py-1.5 font-medium cursor-pointer ${paymentColour[inv.paymentStatus]}`}
          >
            {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabel[s]}</option>)}
          </select>

          {inv.paymentStatus === 'Unpaid' && (
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => { setBookingPickerError(null); setShowBookingPicker(true) }}>
              <Pencil size={15} /> Edit bookings
            </Button>
          )}

          <Button variant="outline" size="sm" className="gap-1.5"
            onClick={() => downloadPdf(id!, inv.invoiceNumber)}>
            <Download size={15} /> Download PDF
          </Button>

          <Button variant="outline" size="sm" className="gap-1.5"
            onClick={() => window.print()}>
            <Printer size={15} /> Print
          </Button>

          <Button
            variant="outline" size="sm" className="gap-1.5"
            onClick={() => setReminderOpen(true)}
            disabled={!reminderTemplate}
          >
            <Bell size={15} /> Send reminder
          </Button>

          {/* Push to Xero */}
          {xeroStatus?.isConnected && (
            inv.xeroInvoiceId ? (
              <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-2.5 py-1.5">
                <Check size={13} />
                Synced to Xero
                {inv.xeroSyncedAt && (
                  <span className="text-green-500 ml-1">
                    · {formatDate(inv.xeroSyncedAt)}
                  </span>
                )}
              </div>
            ) : (
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 pl-3"
                  onClick={() => pushToXeroMutation.mutate()}
                  disabled={pushToXeroMutation.isPending}
                >
                  {pushToXeroMutation.isPending
                    ? <><RefreshCw size={13} className="animate-spin" /> Pushing…</>
                    : <><ExternalLink size={13} /> Push to Xero</>
                  }
                </Button>
                <span className="absolute -top-2 -right-2 text-[9px] font-bold bg-amber-400 text-amber-900 rounded px-1 py-0 leading-4">
                  BETA
                </span>
              </div>
            )
          )}

          {sendConfirm ? (
            <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5">
              <span className="text-sm text-indigo-700">
                Send to <strong>{inv.clientContactEmail}</strong>?
              </span>
              <Button size="sm" className="h-7 gap-1 text-xs"
                onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending}>
                <Check size={13} /> {sendMutation.isPending ? 'Sending…' : 'Confirm'}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-500"
                onClick={() => setSendConfirm(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={() => setSendConfirm(true)}>
              <Send size={15} /> Email to client
            </Button>
          )}
        </div>
      </div>

      {/* ── Invoice document ── */}
      <style>{`@page { size: A4; margin: 0; }`}</style>
      <div className="bg-slate-100 -mx-10 -mb-10 px-10 py-10 print:bg-transparent print:p-0 print:m-0">
      <div className="bg-white w-[210mm] min-h-[297mm] mx-auto shadow-md p-[15mm] print:shadow-none print:p-[15mm]">

        {/* Header: company on left, INVOICE on right */}
        <div className="flex justify-between items-start mb-10">
          <div className="flex flex-col gap-3">
            {inv.companyLogoUrl && (
              <img
                src={inv.companyLogoUrl}
                alt={inv.companyDisplayName ?? 'Company logo'}
                className="h-24 w-auto object-contain object-left"
              />
            )}
            <div>
              {inv.companyDisplayName && (
                <p className="font-bold text-gray-900 text-lg">{inv.companyDisplayName}</p>
              )}
              {companyAddress && (
                <p className="text-sm text-gray-500 mt-0.5">{companyAddress}</p>
              )}
              {inv.companyEmail && (
                <p className="text-sm text-gray-500">{inv.companyEmail}</p>
              )}
              {inv.companyWebsite && (
                <p className="text-sm text-gray-500">{inv.companyWebsite}</p>
              )}
              {inv.companyVatNumber && (
                <p className="text-sm text-gray-500">{inv.taxLabel}: {inv.companyVatNumber}</p>
              )}
              {inv.companyNumber && (
                <p className="text-sm text-gray-500">Co. No: {inv.companyNumber}</p>
              )}
            </div>
          </div>

          <div className="text-right">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">INVOICE</h1>
            <p className="text-gray-500 font-mono text-sm mt-1">{inv.invoiceNumber}</p>
            <div className={`inline-flex mt-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${paymentColour[inv.paymentStatus]}`}>
              {paymentLabel[inv.paymentStatus]}
            </div>
          </div>
        </div>

        {/* Invoice meta + Bill To */}
        <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-8">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Bill to</p>
            <p className="font-semibold text-gray-900">{inv.clientName}</p>
            {inv.clientContactName && (
              <p className="text-sm text-gray-600">{inv.clientContactName}</p>
            )}
            <p className="text-sm text-gray-600">{inv.clientContactEmail}</p>
            {clientAddress && (
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{clientAddress}</p>
            )}
          </div>

          <div className="text-right">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Invoice details</p>
            <table className="ml-auto text-sm">
              <tbody>
                <tr>
                  <td className="text-gray-500 pr-6 py-0.5">Issue date</td>
                  <td className="font-medium text-gray-900">{fmt(inv.issuedDate)}</td>
                </tr>
                {inv.dueDate && (
                  <tr>
                    <td className="text-gray-500 pr-6 py-0.5">Due date</td>
                    <td className="font-medium text-gray-900">{fmt(inv.dueDate)}</td>
                  </tr>
                )}
                {inv.lines.length === 1 && (
                  <tr>
                    <td className="text-gray-500 pr-6 py-0.5">Training date</td>
                    <td className="font-medium text-gray-900">{fmt(inv.lines[0].bookingDate)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Description</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-20">Qty</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-28">Unit price</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map(line => (
              <tr key={line.bookingId} className="border-b border-gray-100">
                <td className="py-4">
                  <p className="font-medium text-gray-900">
                    {line.courseName}
                    {line.pricingModel === 'FlatFee' && (
                      <span className="text-gray-400 font-normal">
                        {' '}(flat fee{line.flatFeeCapacity != null ? `, up to ${line.flatFeeCapacity} people` : ''})
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmt(line.bookingDate)}
                    {line.courseCode && <span className="font-mono">{' · ' + line.courseCode}</span>}
                  </p>
                </td>
                <td className="text-right text-gray-700 py-4">{line.places}</td>
                <td className="text-right text-gray-700 py-4">
                  {line.pricingModel === 'FlatFee' ? '—' : line.unitPrice != null ? formatMoney(line.unitPrice, inv.currency) : '—'}
                </td>
                <td className="text-right font-medium text-gray-900 py-4">{formatMoney(line.amount, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {inv.vatAmount != null && inv.vatAmount > 0 ? (
              <>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-sm text-gray-500 pt-4 pr-0">Subtotal</td>
                  <td className="text-right font-medium text-gray-900 pt-4">{formatMoney(inv.amount, inv.currency)}</td>
                </tr>
                <tr>
                  <td colSpan={2} />
                  <td className="text-right text-sm text-gray-500 py-1 pr-0">{inv.taxLabel} ({inv.vatRate}%)</td>
                  <td className="text-right text-sm text-gray-700 py-1">{formatMoney(inv.vatAmount, inv.currency)}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={2} />
                <td className="text-right text-sm text-gray-500 pt-4 pr-0">Subtotal</td>
                <td className="text-right font-medium text-gray-900 pt-4">{formatMoney(inv.amount, inv.currency)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={2} />
              <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900 pr-0">Total</td>
              <td className="text-right text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-900">{formatMoney(inv.total, inv.currency)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Notes */}
        {inv.notes && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Notes</p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}

        {/* Payment details */}
        {(inv.companyBankName || inv.companyAccountNumber || inv.companyIban) && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Payment details</p>
            <div className="grid gap-x-8 gap-y-1.5 text-sm" style={{ gridTemplateColumns: 'auto 1fr' }}>
              {inv.companyBankName && (
                <>
                  <span className="text-gray-500">Bank</span>
                  <span className="text-gray-900 font-medium">{inv.companyBankName}</span>
                </>
              )}
              {inv.companyAccountName && (
                <>
                  <span className="text-gray-500">Account name</span>
                  <span className="text-gray-900 font-medium">{inv.companyAccountName}</span>
                </>
              )}
              {inv.companyAccountNumber && (
                <>
                  <span className="text-gray-500">Account number</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyAccountNumber}</span>
                </>
              )}
              {inv.companySortCode && (
                <>
                  <span className="text-gray-500">Sort code</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companySortCode}</span>
                </>
              )}
              {inv.companyRoutingNumber && (
                <>
                  <span className="text-gray-500">Routing number (ABA)</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyRoutingNumber}</span>
                </>
              )}
              {inv.companyIban && (
                <>
                  <span className="text-gray-500">IBAN</span>
                  <span className="text-gray-900 font-mono font-medium">{inv.companyIban}</span>
                </>
              )}
            </div>
            {inv.companyPaymentReference && (
              <p className="text-sm text-gray-500 mt-3">{inv.companyPaymentReference}</p>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
    </>
  )
}
